import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import { EMPTY, Observable } from 'rxjs';
import { UserService, CvdDongleService, VersionService } from '../../services';
import { OrderControllerService } from '../../services/order/order-controller.service';
import { CvdDongleAlert, User } from '../../models';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { OverlayModule } from '@angular/cdk/overlay';
import { RouterLink, RouterLinkActive, RouterModule, RouterOutlet } from '@angular/router';
import { PopupTooltipComponent } from '../../modules/insight-popup-tooltip/components/insight-popup-tooltip/popup-tooltip.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { CacheRefreshTimeService } from 'src/app/services/cache-refresh-time-service/cache-refresh-time.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { TimeUtilityService } from 'src/app/services/time-utility/time-utility.service';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { OrderShipmentGroupsList } from '../../models/order-shipment-groups-list';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner/loading-spinner.component';

interface CacheWarnings {
  outdatedWarnings: { name: string; lastRefresh: Date; ageMinutes: number }[];
  nullWarnings: { name: string }[];
}

@Component({
  selector: 'insight-root',
  templateUrl: './insight.component.html',
  styleUrls: ['./insight.component.scss'],
  imports: [
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    MatSelectModule,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    MatToolbarModule,
    OverlayModule,
    PopupTooltipComponent,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatSelectModule,
    MatListModule,
    OverlayModule,
    MatSidenavModule,
    MatDatepickerModule,
    MatNativeDateModule,
    DatePipe,
    LoadingSpinnerComponent
  ]
})
export class InsightComponent implements OnInit {
  // == DEPENDENCY INJECTION ===============================
  private readonly versionService = inject(VersionService);
  private readonly userService = inject(UserService);
  private readonly orderControllerService = inject(OrderControllerService);
  private readonly cvdDongleService = inject(CvdDongleService);
  private readonly cacheRefreshService = inject(CacheRefreshTimeService);
  protected readonly timeService = inject(TimeUtilityService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly environment = inject(EnvironmentService);

  // == MEMBER VARIABLES =========================================
  protected user = signal<User>(undefined);
  protected applicationVersion = signal<string>('');
  public cvdAlertMessages: string[] = [];
  protected readonly MAXIMUM_CACHE_AGE_MINUTES = 30;
  protected environmentName: string;
  protected allOrderShipmentGroupsList = signal<OrderShipmentGroupsList>(new OrderShipmentGroupsList());
  // Set once the first order shipment response arrives so the "cache not ready" warning
  // doesn't flash on every page load before we know the cache state
  private readonly hasReceivedOrderData = signal<boolean>(false);

  // == LIFECYCLE =========================================
  ngOnInit() {
    this.environmentName = this.environment.getEnvironmentName();

    this.getVersion()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ver) => {
        this.applicationVersion.set(ver);
      });

    this.getServerNowTime()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((time) => {
        console.log(`Date and time currently on the server ${time}`);
      });

    this.getUser()
      .pipe(
        tap((usr) => this.user.set(usr)),
        // make sure this permission is in line with whatever is shown on the HTML that shows the warnings
        // only operations and editors should see the CVD dongle alerts
        filter((usr) => usr.permissions.insightOperations || usr.permissions.insightEditor),
        switchMap(() => this.cvdDongleService.getCvdDongleAlerts().pipe(catchError(() => EMPTY))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((alerts: CvdDongleAlert[]) => {
        // the usable message(s) to display in the UI is buried in the message child
        alerts.forEach((cvdAlert) => {
          if (cvdAlert.alertMessages.length > 0) {
            cvdAlert.alertMessages.forEach((message) => {
              this.cvdAlertMessages.push(message.trim()); // messages sometimes precede with white space
            });
          }
        });
      });

    // For the order shipment stats
    this.orderControllerService.orderDataRefreshedEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.allOrderShipmentGroupsList.set(this.orderControllerService.getAllOrderShipments());
        this.hasReceivedOrderData.set(true);
      });

    // we only need to grab these version mappings once
    // they are useful for telling what ARC codes are with line numbers for ADEBs order lines
    this.orderControllerService.getArcVersionMappings();
    this.orderControllerService.refreshAllShipmentsOneTime(); // grab data immediately since we just started our application
  }

  // == COMPUTED SIGNALS =========================================
  protected cacheMessages = computed<CacheWarnings>(() => {
    const cacheWarnings: CacheWarnings = {
      outdatedWarnings: [],
      nullWarnings: [],
    };

    if (this.cacheRefreshService.adebsdbLastRefreshTime()) {
      const adebsdbCacheAge = this.timeService.getDateAge(this.cacheRefreshService.adebsdbLastRefreshTime());
      if (adebsdbCacheAge >= this.MAXIMUM_CACHE_AGE_MINUTES) {
        cacheWarnings.outdatedWarnings.push({
          name: 'ADEBSDB',
          lastRefresh: this.cacheRefreshService.adebsdbLastRefreshTime(),
          ageMinutes: adebsdbCacheAge,
        });
      }
    } else {
      cacheWarnings.nullWarnings.push({
        name: 'ADEBSDB',
      });
    }

    if (this.cacheRefreshService.sgcLastRefreshTime()) {
      const sgcCacheAge = this.timeService.getDateAge(this.cacheRefreshService.sgcLastRefreshTime());
      if (sgcCacheAge >= this.MAXIMUM_CACHE_AGE_MINUTES) {
        cacheWarnings.outdatedWarnings.push({
          name: 'SGC',
          lastRefresh: this.cacheRefreshService.sgcLastRefreshTime(),
          ageMinutes: sgcCacheAge,
        });
      }
    } else {
      cacheWarnings.nullWarnings.push({
        name: 'SGC',
      });
    }

    return cacheWarnings;
  });

  protected readonly isOrderCacheNotReady = computed<boolean>(() => {
    return this.hasReceivedOrderData() && this.allOrderShipmentGroupsList().cacheProcessingTime === null;
  });

  // The last refresh could not load the missing content entries, so the orders were processed with
  // older entries (or none at all if they have never loaded)
  protected readonly isMissingContentStale = computed<boolean>(() => {
    return this.hasReceivedOrderData() && this.allOrderShipmentGroupsList().lastMissingContentRefreshError !== null;
  });

  protected readonly hasOrderShipmentsWarning = computed<boolean>(() => {
    return this.isOrderCacheNotReady() || this.isMissingContentStale();
  });

  protected readonly formattedMissingContentRefreshWarning = computed<string>(() => {
    const refreshTime = this.allOrderShipmentGroupsList().missingContentRefreshTime;
    return refreshTime === null
      ? 'The missing content entries have never loaded, so orders are not showing any missing content.'
      : `The last missing content update failed, so orders are showing missing content as of ${new Date(refreshTime).toLocaleString()}.`;
  });

  protected isADEBSAtLimit = computed<boolean>(() => {
    return this.allOrderShipmentGroupsList().totalADEBSCount >= this.allOrderShipmentGroupsList().maxAllOrderShipments;
  });

  protected isSGCAtLimit = computed<boolean>(() => {
    return this.allOrderShipmentGroupsList().totalSGCCount >= this.allOrderShipmentGroupsList().maxAllOrderShipments;
  });

  protected formattedCacheProcessingTime = computed<string>(() => {
    return new Date(this.allOrderShipmentGroupsList().cacheProcessingTime).toLocaleString();
  });

  protected formattedFinalProcessingTime = computed<string>(() => {
    return this.allOrderShipmentGroupsList().lastCacheProcessingTimeSeconds === null
      ? null
      : `${this.allOrderShipmentGroupsList().lastCacheProcessingTimeSeconds} seconds`;
  });

  protected formattedMaxAllOrderShipments = computed<string>(() => {
    return this.allOrderShipmentGroupsList().maxAllOrderShipments.toLocaleString();
  });

  protected formattedADEBSCount = computed<string>(() => {
    return `${this.allOrderShipmentGroupsList().totalADEBSCount.toLocaleString()}${this.isADEBSAtLimit() ? " (at limit)" : ""}`;
  });

  protected formattedSGCCount = computed<string>(() => {
    return `${this.allOrderShipmentGroupsList().totalSGCCount.toLocaleString()}${this.isSGCAtLimit() ? " (at limit)" : ""}`;
  });

  protected formattedADEBSCacheTime = computed<string>(() => {
    let refreshTime = this.cacheRefreshService.adebsdbLastRefreshTime();
    return refreshTime ? refreshTime.toLocaleString() : null;
  });

  protected formattedSGCCacheTime = computed<string>(() => {
    let refreshTime = this.cacheRefreshService.sgcLastRefreshTime();
    return refreshTime ? refreshTime.toLocaleString() : null;
  });

  protected formattedNextCacheProcessingStartedTime = computed<string>(() => {
    return `Currently running final processing for the next cache update. Started at ${new Date(this.allOrderShipmentGroupsList().nextCacheProcessingStartedTime).toLocaleString()}`;
  });

  protected formattedLastCacheProcessingError = computed<string>(() => {
    return `The last final processing run had an error: ${this.allOrderShipmentGroupsList().lastCacheProcessingError}`;
  });

  // == HELPER METHODS =========================================
  public signOut() {
    this.userService.signOut();
  }

  private getUser(): Observable<User> {
    return this.userService.getUser().pipe(catchError(() => EMPTY));
  }

  private getVersion(): Observable<string> {
    return this.versionService.getVersion().pipe(catchError(() => EMPTY));
  }

  private getServerNowTime(): Observable<string> {
    return this.versionService.getServerNowTime().pipe(catchError(() => EMPTY));
  }
}
